import React from 'react';
import { flushSync } from 'react-dom';
import { createRoot, hydrateRoot } from 'react-dom/client';

window.React = React;

const CLASS_NAME_ATTR = 'data-react-class';
const PROPS_ATTR = 'data-react-props';
const RENDER_ATTR = 'data-hydrate';

// React 18 renders through a root created once per container. Every pack calls
// mountComponents with the registry built so far, so the same node comes back
// several times: its root is kept here and asked to render again, as the
// legacy render() did when called twice on one node.
//
// The legacy render() was also synchronous, and the scripts that run right
// after mountComponents (tooltips, popper, the AJAX layouts) expect the
// components' markup to be in the DOM already: flushSync keeps it that way.
const roots = new WeakMap();

export function mountComponents(context) {
  const keys = Object.keys(context);

  for (const key of keys) {
    const selector = `[${CLASS_NAME_ATTR}="${key}"]`;
    const nodes = document.querySelectorAll(selector);

    for (let i = 0; i < nodes.length; ++i) {
      const node = nodes[i];
      const component = context[key];
      const constructor = component.__esModule ? context[key].default : context[key];
      const propsJson = node.getAttribute(PROPS_ATTR);
      const props = propsJson && JSON.parse(propsJson);
      const doHydrate = node.getAttribute(RENDER_ATTR);

      if (!constructor) {
        const message = `Cannot find component in current context: '${key}'`;

        if (console && console.log) {
          console.log(`%c[react-rails] %c${message} for element,`, 'font-weight: bold', 'font-weight: normal', node);
          console.log('%c[react-rails] %cCurrent context:', 'font-weight: bold', 'font-weight: normal', context);
        }

        throw new Error(`${message}. Make sure your component is available to render.`);
      }

      const reactComponent = window.React.createElement(constructor, props);
      const root = roots.get(node);

      if (root) {
        flushSync(() => root.render(reactComponent));
      } else if (doHydrate) {
        roots.set(node, hydrateRoot(node, reactComponent));
      } else {
        const newRoot = createRoot(node);

        flushSync(() => newRoot.render(reactComponent));
        roots.set(node, newRoot);
      }
    }
  }
}

export function unMountComponents(context) {
  const keys = Object.keys(context);

  for (const key of keys) {
    const selector = `[${CLASS_NAME_ATTR}="${key}"]`;
    const nodes = document.querySelectorAll(selector);

    for (let i = 0; i < nodes.length; ++i) {
      const node = nodes[i];
      const root = roots.get(node);

      if (root) {
        root.unmount();
        roots.delete(node);
      }
    }
  }
}
